import { faCircle } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, Container, Grid, Typography } from "@mui/material"
import { styled, useTheme } from "@mui/material/styles"
import { useApiGet, useApiPut } from "auth/useApi2"
import { AppContext } from "components/AppContextProvider"
import Button from "components/Button"
import ClpTable from "components/ClpTable"
import { SelectColumnFilter } from "components/ClpTable/ClpTableFilterTypes"
import TableButton from "components/ClpTable/components/actions/TableButton/TableButton"
import { StyledTableButtonGroup } from "components/ClpTable/components/actions/TableButton/tableButtonGroup.styled"
import { TableButtonTypeEnum } from "components/ClpTable/components/actions/TableButton/tableButtonProps.interface"
import { GlobalLoaderContext } from "components/GlobalLoadingProvider"
import { apiEndpoints } from "generated/apiEndpoints"
import { allRoutes } from "generated/routes"
import { t } from "i18next"
import moment from "moment"
import { useContext, useEffect, useMemo } from "react"
import { Link, useHistory } from "react-router-dom"
import { StyledTableContainer } from "shared/styled/Global"
import { SidebarDeviceForm } from "./components/SidebarDeviceForm"

const StyledFontAwesomeIcon = styled(FontAwesomeIcon)(({ theme, isActive }) => ({
  color: isActive === "Aktiv" ? theme.palette.success.main : theme.palette.error.main
}))

const Register = () => {
  const routerHistory = useHistory()
  const appContext = useContext(AppContext)
  const [deviceResult, getDevices] = useApiGet()
  const [deviceItemResult, getDevice] = useApiGet()
  const [editDeviceResult, editDevice] = useApiPut()
  const loadingContext = useContext(GlobalLoaderContext)
  const theme = useTheme()

  useEffect(() => {
    getDevices(apiEndpoints.systemadmindeviceregistry.listdevices)
  }, [getDevices])

  useEffect(() => {
    loadingContext.setLoading(() => deviceResult.status === 1)
  })

  const openDeviceForm = (id) => {
    getDevice(apiEndpoints.systemadmindeviceregistry.getdevicebyid.replace("{id}", id))
  }

  const handleDeviceFormSubmit = (objectToSave) => {
    editDevice(apiEndpoints.systemadmindeviceregistry.updatedevice, objectToSave)
  }

  useEffect(() => {
    if (editDeviceResult.status === 2) {
      appContext.toggleSideBarMulti("device", false)
      getDevices(apiEndpoints.systemadmindeviceregistry.listdevices)
    }
  }, [editDeviceResult.status])

  const columns = useMemo(
    () => [
      {
        accessorKey: "deviceId",
        header: t("global.table.regnr", "Reg No")
      },
      {
        accessorKey: "internalDeviceId",
        header: t("global.internal_number", "Internal number")
      },
      {
        header: t("global.connected_devices", "Connected devices"),
        accessorFn: (row) => {
          return String(row?.equipments?.length > 0)
        },
        cell: ({ row }) => {
          var label = row.original.equipments
          return <>{label}</>
        },
        meta: {
          filterHeader: t("global.connected_devices", "Connected devices"),
          mainFilter: true,
          filter: SelectColumnFilter,
          filterOptions: [
            {
              name: t("global.equipment_connected", "Equipment connected"),
              value: "true"
            },
            {
              name: t("global.no_equipment", "No equipment"),
              value: "false"
            }
          ]
        },
        filter: "customFilter"
      },
      {
        accessorKey: "companyName",
        header: t("global.company", "Company"),
        meta: {
          mainFilter: true,
          filter: SelectColumnFilter
        }
      },
      {
        accessorKey: "technicalPackageName",
        header: t("global.technology_package", "Technology package"),
        meta: {
          mainFilter: true,
          filter: SelectColumnFilter
        }
      },
      {
        accessorFn: (row) => {
          return row.equipmentOwnedByCustomer ? t("global.customer", "Customer") : "Cloudplanning"
        },
        header: t("global.owner_of_equipment", "Owner of equipment"),
        meta: {
          filter: SelectColumnFilter
        }
      },
      {
        accessorFn: (row) => {
          return row.created && moment(row.created).isValid()
            ? moment(row.created).format("YYYY-MM-DD hh:mm")
            : ""
        },
        header: t("global.created", "Created")
      },
      {
        accessorKey: "status",
        header: "Status",
        filterFn: (row, id, filterValue) => {
          // Sadly this slows down filtering a little bit
          return row.original.status === filterValue
        },
        meta: {
          filter: SelectColumnFilter,
          mainFilter: true,
          align: "center",
          filterOptions: [
            {
              name: t("global.active2", "Active"),
              value: "Aktiv"
            },
            {
              name: t("global.inactive", "Inactive"),
              value: "Inaktiv"
            }
          ]
        },
        cell: ({ row }) => <StyledFontAwesomeIcon isActive={row.original.status} icon={faCircle} />
      },
      {
        accessorKey: "edit",
        disablePadding: true,
        header: "",
        align: "right",
        cell: ({ cell }) => {
          return (
            <StyledTableButtonGroup>
              <TableButton
                type={TableButtonTypeEnum.Edit}
                handleOnClick={() => {
                  openDeviceForm(cell.row.original.id)
                  appContext.toggleSideBarMulti("device", true)
                }}
              />
            </StyledTableButtonGroup>
          )
        }
      }
    ],
    [theme]
  )

  return (
    <div>
      {deviceItemResult.status === 2 && (
        <SidebarDeviceForm
          result={deviceItemResult}
          onSubmit={handleDeviceFormSubmit}
          updateResult={editDeviceResult}
        />
      )}

      <Container>
        <Box>
          <Grid container direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h1">{t("global.units", "Units")}</Typography>
            <Button
              component={Link}
              to={allRoutes.SystemadminDevicesRegisterAdd()}
              color="primary"
              variant="contained">
              {t("global.add_device", "Add device")}
            </Button>
          </Grid>
        </Box>
      </Container>
      <StyledTableContainer>
        <ClpTable
          columns={columns}
          onRowClick={(row) => routerHistory.push(allRoutes.SystemadminDevicesRegisterEdit(row.original.id))}
          rows={deviceResult.status === 2 && deviceResult.data}
          loadingState={deviceResult.status}
        />
      </StyledTableContainer>
    </div>
  )
}

export default Register
